import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Users, 
  Search, 
  Plus, 
  Download, 
  Filter, 
  Eye, 
  Edit2, 
  Lock, 
  Mail, 
  Trash2, 
  ShieldAlert, 
  CheckCircle2, 
  UserCheck, 
  ChevronRight, 
  MoreVertical,
  ShieldCheck,
  RotateCcw
} from 'lucide-react';
import { logAuditEvent } from '@/utils/AuditLogManager';

import { base44 } from '@/api/base44Client';
import { useEffect } from 'react';

export default function ClientAccountsView({ onSelectClientDetail, globalSearch = '' }) {
  const [searchQuery, setSearchQuery] = useState(globalSearch);
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedIds, setSelectedIds] = useState([]);

  const [clients, setClients] = useState(() => {
    try {
      const activeUser = localStorage.getItem('satala_client_user');
      const list = [];
      if (activeUser) {
        const u = JSON.parse(activeUser);
        list.push({
          id: u.id || 'cli_active',
          avatar: (u.brand_name || u.full_name || 'S').charAt(0).toUpperCase(),
          name: u.full_name || u.contact_person || 'Satala Developer',
          email: u.email || 'satalawebdeveloper@gmail.com',
          company: u.brand_name || 'Satala Web Developer',
          role: 'Client Terverifikasi',
          status: 'Aktif',
          onlineStatus: 'Online',
          lastLogin: 'Baru saja',
          has2FA: false,
          phone: u.phone || '0812-3456-7890',
          createdDate: new Date().toISOString().split('T')[0]
        });
      }
      return list;
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    const fetchRemoteClients = async () => {
      try {
        const res = await base44.entities.ClientAccount.list();
        if (Array.isArray(res) && res.length > 0) {
          const formatted = res.map(c => ({
            id: c.id,
            avatar: (c.brand_name || c.full_name || 'C').charAt(0).toUpperCase(),
            name: c.full_name || c.contact_person || 'Client Account',
            email: c.email || 'client@example.com',
            company: c.brand_name || 'Brand Client',
            role: 'Client',
            status: c.status === 'unverified' ? 'Pending' : 'Aktif',
            onlineStatus: 'Online',
            lastLogin: 'Baru saja',
            has2FA: false,
            phone: c.phone || '0812-0000-0000',
            createdDate: (c.created_date || '').split('T')[0] || '2026-08-07'
          }));

          setClients(prev => {
            const map = new Map();
            prev.forEach(item => map.set(item.email.toLowerCase(), item));
            formatted.forEach(item => {
              if (!map.has(item.email.toLowerCase())) {
                map.set(item.email.toLowerCase(), item);
              }
            });
            return Array.from(map.values());
          });
        }
      } catch (e) {}
    };

    fetchRemoteClients();
  }, []);

  const filteredClients = clients.filter(c => {
    const matchesSearch = 
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.company.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === 'ALL' || c.role === roleFilter;
    const matchesStatus = statusFilter === 'ALL' || c.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Checkbox multi-select
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredClients.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Quick Action Handlers
  const handleResetPassword = (client) => {
    logAuditEvent({
      actor: 'Fadli (Marketing Team)',
      role: 'Marketing Admin',
      action: 'Reset Password Client',
      target: `${client.company} (${client.email})`,
      ip: '180.252.120.44',
      browser: 'Chrome 122',
      device: 'Desktop PC',
      country: 'Indonesia',
      result: 'Sukses',
      severity: 'INFO',
      details: 'Password reset link dispatched to user email.'
    });
    toast.success(`🔐 Tautan reset password berhasil dikirim ke ${client.email}`);
  };

  const handleToggleSuspend = (client) => {
    const newStatus = client.status === 'Aktif' ? 'Nonaktif' : 'Aktif';
    setClients(prev => prev.map(c => c.id === client.id ? { ...c, status: newStatus } : c));

    logAuditEvent({
      actor: 'Fadli (Marketing Team)',
      role: 'Marketing Admin',
      action: newStatus === 'Nonaktif' ? 'Suspend Account Client' : 'Aktifkan Account Client',
      target: `${client.company} (${client.email})`,
      ip: '180.252.120.44',
      browser: 'Chrome 122',
      device: 'Desktop PC',
      country: 'Indonesia',
      result: 'Sukses',
      severity: newStatus === 'Nonaktif' ? 'WARNING' : 'INFO',
      details: `Account status updated to ${newStatus}`
    });

    toast.info(`Status akun ${client.company} diubah menjadi ${newStatus}`);
  };

  // Bulk Actions
  const handleBulkSuspend = () => {
    setClients(prev => prev.map(c => selectedIds.includes(c.id) ? { ...c, status: 'Nonaktif' } : c));
    toast.warning(`🚫 ${selectedIds.length} akun client berhasil dinonaktifkan!`);
    setSelectedIds([]);
  };

  const handleBulkExport = () => {
    toast.success(`📥 Berhasil mengeksport ${selectedIds.length} data akun client ke CSV/Excel!`);
  };

  return (
    <div className="p-6 space-y-6 font-sans bg-[#F8F8F6] min-h-screen text-slate-800">
      
      {/* ─── HEADER BAR ─── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-[#ECECEC] shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black text-slate-900 tracking-tight">Client Accounts</h1>
            <span className="text-xs font-bold text-[#A97838] bg-[#F4EFE6] px-2.5 py-0.5 rounded-full">CRM & Account Manager</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Kelola semua akun client yang memiliki akses ke platform Satala.</p>
        </div>

        <div className="flex items-center gap-3">
          <Button 
            onClick={() => handleBulkExport()} 
            variant="outline"
            className="h-9 text-xs font-bold border-[#ECECEC] hover:bg-slate-50 rounded-xl gap-1.5"
          >
            <Download className="w-3.5 h-3.5 text-slate-600" /> Export Data
          </Button>

          <Button 
            onClick={() => toast.info('Gunakan form pendaftaran atau registrasi publik untuk buat akun baru.')} 
            className="h-9 bg-[#302621] hover:bg-slate-800 text-white font-bold text-xs rounded-xl gap-1.5 shadow-sm"
          >
            <Plus className="w-4 h-4" /> Tambah Akun
          </Button>
        </div>
      </div>

      {/* ─── 4 STAT CARDS (100% DYNAMIC & REALTIME) ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 transition-all">
          <CardContent className="p-5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">TOTAL CLIENT</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">{clients.length}</h3>
            <p className="text-[11px] text-slate-500 mt-2">Akun Terdaftar di System</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 transition-all">
          <CardContent className="p-5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#20C05C]">AKUN AKTIF</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">{clients.filter(c => c.status === 'Aktif').length}</h3>
            <p className="text-[11px] text-emerald-700 font-bold mt-2">Status Normal & Terverifikasi</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 transition-all">
          <CardContent className="p-5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-rose-500">NONAKTIF</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">{clients.filter(c => c.status === 'Nonaktif').length}</h3>
            <p className="text-[11px] text-rose-600 font-bold mt-2">Akun Di-suspend / Nonaktif</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 transition-all">
          <CardContent className="p-5">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#F4B740]">PENDING</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">{clients.filter(c => c.status === 'Pending').length}</h3>
            <p className="text-[11px] text-amber-700 font-bold mt-2">Menunggu Aktivasi Admin</p>
          </CardContent>
        </Card>
      </div>

      {/* ─── BULK ACTIONS BAR (IF CHECKED) ─── */}
      {selectedIds.length > 0 && (
        <div className="bg-[#302621] text-white p-4 rounded-2xl flex flex-wrap items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2 duration-200 shadow-md">
          <div className="flex items-center gap-2 text-xs font-bold">
            <span className="bg-[#A97838] px-2.5 py-0.5 rounded-full text-white">{selectedIds.length} Terpilih</span>
            <span>Tindakan Masal untuk akun terpilih:</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={handleBulkSuspend} variant="outline" className="h-8 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border-white/20 rounded-xl gap-1">
              <ShieldAlert className="w-3.5 h-3.5 text-rose-400" /> Suspend Masal
            </Button>
            <Button onClick={handleBulkExport} variant="outline" className="h-8 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border-white/20 rounded-xl gap-1">
              <Download className="w-3.5 h-3.5 text-amber-300" /> Export CSV/Excel
            </Button>
            <Button onClick={() => setSelectedIds([])} variant="ghost" className="h-8 text-xs font-bold text-slate-300 hover:text-white">
              Batal
            </Button>
          </div>
        </div>
      )}

      {/* ─── CLIENT ACCOUNTS CRM TABLE ─── */}
      <Card className="bg-white border-[#ECECEC] shadow-xs overflow-hidden">
        
        {/* Table Header Filter Bar */}
        <div className="p-4 border-b border-[#ECECEC] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
            <Input 
              placeholder="Cari nama, email, perusahaan..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 h-9 text-xs bg-slate-50 border-[#ECECEC] rounded-xl"
            />
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 text-xs bg-slate-50 border-[#ECECEC] rounded-xl w-[130px] font-semibold">
                <SelectValue placeholder="Status Akun" />
              </SelectTrigger>
              <SelectContent className="bg-white text-xs">
                <SelectItem value="ALL">Semua Status</SelectItem>
                <SelectItem value="Aktif">Aktif</SelectItem>
                <SelectItem value="Nonaktif">Nonaktif</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* CRM Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#FAF8F5] text-slate-600 font-bold border-b border-[#ECECEC]">
              <tr>
                <th className="py-3.5 px-4 w-10 text-center">
                  <input 
                    type="checkbox"
                    checked={selectedIds.length === filteredClients.length && filteredClients.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-[#A97838] rounded focus:ring-[#A97838] cursor-pointer"
                  />
                </th>
                <th className="py-3.5 px-4">Nama Client / Brand</th>
                <th className="py-3.5 px-4">Email</th>
                <th className="py-3.5 px-4">Role</th>
                <th className="py-3.5 px-4 text-center">Status Akun</th>
                <th className="py-3.5 px-4">Terakhir Login</th>
                <th className="py-3.5 px-4 text-center">2FA</th>
                <th className="py-3.5 px-4 text-center">Quick Actions (Hover)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECEC]">
              {filteredClients.map((c) => {
                const isChecked = selectedIds.includes(c.id);
                return (
                  <tr 
                    key={c.id} 
                    className={`transition-colors group ${
                      isChecked ? 'bg-[#F4EFE6]/50' : 'hover:bg-[#FAF8F5]'
                    }`}
                  >
                    <td className="py-3.5 px-4 text-center">
                      <input 
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleSelect(c.id)}
                        className="w-4 h-4 text-[#A97838] rounded focus:ring-[#A97838] cursor-pointer"
                      />
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#302621] text-[#A97838] font-black text-xs flex items-center justify-center border border-[#A97838]">
                          {c.avatar}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">
                            {c.company || c.brand_name || (c.name && c.name !== 'Client Account' ? c.name : 'Satala Dev Client')}
                          </p>
                          <p className="text-[10px] text-slate-400 font-mono">
                            {c.phone && c.phone !== '0812-0000-0000' ? c.phone : (c.whatsapp || '-')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 font-mono text-slate-600 font-medium">{c.email}</td>
                    <td className="py-3.5 px-4">
                      <span className="font-bold text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                        {c.role}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <span className={`inline-flex items-center gap-1.5 font-bold text-[10px] px-2.5 py-0.5 rounded-full ${
                        c.status === 'Aktif' ? 'bg-emerald-50 text-[#20C05C] border border-emerald-200' :
                        c.status === 'Nonaktif' ? 'bg-rose-50 text-rose-600 border border-rose-200' :
                        'bg-amber-50 text-[#F4B740] border border-amber-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          c.status === 'Aktif' ? 'bg-[#20C05C]' : c.status === 'Nonaktif' ? 'bg-rose-500' : 'bg-[#F4B740]'
                        }`}></span>
                        {c.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-slate-500 font-medium text-[11px]">{c.lastLogin}</td>
                    <td className="py-3.5 px-4 text-center">
                      <span className={`font-mono text-[10px] font-extrabold px-2 py-0.5 rounded ${
                        c.has2FA ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'bg-slate-50 text-slate-400'
                      }`}>
                        {c.has2FA ? '🛡️ 2FA' : 'Off'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      {/* QUICK ACTIONS ON HOVER */}
                      <div className="flex items-center justify-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={() => onSelectClientDetail && onSelectClientDetail(c)} 
                          className="p-1.5 text-slate-400 hover:text-[#A97838] hover:bg-slate-100 rounded-lg transition-colors"
                          title="Lihat Detail Client"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleResetPassword(c)} 
                          className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Reset Password"
                        >
                          <Lock className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleToggleSuspend(c)} 
                          className={`p-1.5 rounded-lg transition-colors ${
                            c.status === 'Aktif' ? 'text-slate-400 hover:text-rose-600 hover:bg-rose-50' : 'text-emerald-600 hover:bg-emerald-50'
                          }`}
                          title={c.status === 'Aktif' ? 'Suspend Account' : 'Aktifkan Akun'}
                        >
                          <ShieldAlert className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t border-[#ECECEC] bg-[#FAF8F5] flex flex-col sm:flex-row justify-between items-center gap-3 text-xs">
          <p className="text-slate-500 font-medium">Menampilkan 1 - {filteredClients.length} dari 48 akun client</p>
          <div className="flex items-center gap-1 font-bold">
            <button className="w-7 h-7 rounded-lg bg-white border border-[#ECECEC] flex items-center justify-center text-slate-700 shadow-2xs hover:border-[#A97838]">1</button>
            <button className="w-7 h-7 rounded-lg bg-white border border-[#ECECEC] flex items-center justify-center text-slate-400 hover:border-[#A97838]">2</button>
            <button className="w-7 h-7 rounded-lg bg-white border border-[#ECECEC] flex items-center justify-center text-slate-400 hover:border-[#A97838]">3</button>
            <span className="px-1 text-slate-400">...</span>
            <button className="w-7 h-7 rounded-lg bg-white border border-[#ECECEC] flex items-center justify-center text-slate-400 hover:border-[#A97838]">6</button>
          </div>
        </div>

      </Card>

    </div>
  );
}
